<template>
    <div :id="id ? id : _uid + '-dropdown'" class="FormSelect" @keydown.tab="tabKeyPressed = true" @blur.capture="handleBlur">
        <div class="FormSelect__control">
            <span v-if="value" :id="_uid + '-label'" class="FormSelect__label" :aria-label="placeholder" v-text="placeholderText"></span>
            <span :id="id + '-content'" class="u-visually-hidden" v-text="value"></span>
            <button type="button"
                    ref="button"
                    :id="_uid + '-button'"
                    aria-haspopup="listbox"
                    :aria-labelledby="_uid + '-label ' + _uid + '-button'"
                    :aria-expanded="optionsVisible"
                    :aria-invalid="invalid"
                    :aria-required="mandatory"
                    :aria-describedby="ariaErrorElementId"
                    class="FormSelect__button"
                    @click="toggleOptions"
                    @keyup.up.down.prevent="showOptions"
                    @keyup.up.prevent="selectPrevOption"
                    @keyup.down.prevent="selectNextOption">
                {{ optionValues[activeOptionIndex] }}
                <span v-if="!value" class="FormSelect__placeholder" v-text="placeholderText"></span>
                <svg-angle class="FormSelect__icon" :class="{ 'FormSelect__icon--rotate-180': optionsVisible }"></svg-angle>
            </button>
            <!-- Focus trap for iOS keyboard navigation. -->
            <input v-if="!tabKeyPressed" aria-hidden="true" class="u-visually-hidden" @focus="handleFocusTrap">
            <ul :id="_uid + '-optionList'"
                v-show="optionsVisible"
                ref="options"
                tabindex="-1"
                role="listbox"
                :aria-activedescendant="activeDescendant"
                class="FormSelect__options"
                @focus="setupFocus"
                @keydown.up.prevent.stop="selectPrevOption"
                @keydown.down.prevent.stop="selectNextOption"
                @keydown.enter.prevent.stop="closeAndChange"
                @keydown.esc.prevent.stop="reset"
                @keydown.space="handleSpace"
                @keydown="search">
                <li v-for="(option, index, i) in options"
                    :id="_uid + '-option-' + index"
                    :key="'option-' + i"
                    :aria-selected="optionKeys[activeOptionIndex] === index ? 'true' : 'false'"
                    :class="optionKeys[activeOptionIndex] === index && 'has-focus'"
                    class="FormSelect__option" role="option"
                    @click="handleOptionClick(option)" v-text="option">
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import SvgAngle from './SvgAngle.vue'
import Vue from 'vue'

let resetKeysSoFarTimer

export default {
  name: 'FormSelect',
  components: {
    SvgAngle
  },
  model: {
    event: 'change'
  },
  props: {
    id: {
      type: String
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    options: {
      type: Object,
      default: () => {}
    },
    value: {
      type: [String, Number],
      default: ''
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    ariaErrorElementId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      keysSoFar: '',
      tabKeyPressed: false,
      optionsVisible: false,
      internalValue: this.value
    }
  },
  computed: {
    activeOptionIndex () {
      return this.optionKeys.findIndex(
        x => x.value === this.internalValue || x === this.internalValue
      )
    },
    prevOptionIndex () {
      const next = this.activeOptionIndex - 1
      return next >= 0 ? next : 0
    },
    nextOptionIndex () {
      const next = this.activeOptionIndex + 1
      return next <= Object.keys(this.options).length - 1 ? next : Object.keys(this.options).length - 1
    },
    activeDescendant () {
      return this._uid + '-option-' + this.optionKeys[this.activeOptionIndex]
    },
    optionKeys () {
      return Object.keys(this.options)
    },
    optionValues () {
      return Object.values(this.options)
    },
    placeholderText () {
      return this.mandatory ? this.placeholder + ' *' : this.placeholder
    }
  },
  methods: {
    handleSpace: function (e) {
      e.preventDefault()
      e.stopPropagation()
    },
    findOptionIndex (optionValue) {
      return this.optionValues.findIndex(
        x => x.value === optionValue || x === optionValue
      )
    },
    handleFocusTrap (e) {
      this.optionsVisible = true
      this.$refs.button.focus()
    },
    handleOptionClick (option) {
      const clickedIndex = this.findOptionIndex(option)
      if (this.optionKeys[clickedIndex] !== this.internalValue) {
        this.internalValue = this.optionKeys[clickedIndex]
      }
      this.closeAndChange()
    },
    handleBlur (e) {
      let target = e.relatedTarget

      // Necessary for IE11 since relatedTarget is not consistent there
      if (target === null) {
        target = document.activeElement
      }

      if (this.$el.contains(target)) return

      this.hideOptions()
      this.resetIntervalValue()
    },
    toggleOptions () {
      this.optionsVisible ? this.hideOptions() : this.showOptions()
    },
    async showOptions () {
      await this.$nextTick()
      this.$refs.options.focus()
      Vue.nextTick(this.focusList)
      $('#' + this._uid + '-button')[0].classList.add('FormSelect__button--active')
    },
    hideOptions () {
      this.optionsVisible = false
      $('#' + this._uid + '-button')[0].classList.remove('FormSelect__button--active')
    },
    async reset () {
      this.hideOptions()
      this.resetIntervalValue()
      await this.$nextTick()
      this.$refs.button.focus()
    },
    async closeAndChange () {
      this.hideOptions()
      if (this.value !== this.optionKeys[this.activeOptionIndex]) {
        this.$emit('change', this.optionKeys[this.activeOptionIndex])
      }
      await this.$nextTick()
      this.$refs.button.focus()
    },
    setupFocus () {
      if (this.value) return
      this.internalValue = this.optionKeys[0]
      this.trackSelectedDropdownOption(0)
    },
    async selectPrevOption () {
      this.internalValue = this.optionKeys[this.prevOptionIndex]
      await this.$nextTick()
      this.trackSelectedDropdownOption(this.activeOptionIndex)
    },
    async selectNextOption () {
      this.internalValue = this.optionKeys[this.nextOptionIndex]
      await this.$nextTick()
      this.trackSelectedDropdownOption(this.activeOptionIndex)
    },
    resetIntervalValue: function () {
      if (this.value !== this.internalValue) {
        this.internalValue = this.value
      }
    },
    search (e) {
      clearTimeout(resetKeysSoFarTimer)
      // No alphanumeric key was pressed.
      if (e.key.length > 1) return

      resetKeysSoFarTimer = setTimeout(() => {
        this.keysSoFar = ''
      }, 500)

      this.keysSoFar += e.key
      const matchingOption = this.optionValues.find(x =>
        (x.value || x).toLowerCase().startsWith(this.keysSoFar.toLowerCase())
      )
      if (!matchingOption) return

      this.internalValue = this.optionKeys[this.findOptionIndex(matchingOption)]
      this.trackSelectedDropdownOption(this.findOptionIndex(matchingOption))
    },
    focusList () {
      this.optionsVisible = true
      this.trackSelectedDropdownOption(this.activeOptionIndex)
    },
    trackSelectedDropdownOption: async function (optionIndex) {
      const uid = this._uid
      const index = this.optionKeys[optionIndex]
      const optionList = $('#' + uid + '-optionList')
      const selectedOption = $('#' + uid + '-option-' + index)
      await this.$nextTick()
      optionList.focus()
      optionList.scrollTop(selectedOption.index() * selectedOption.outerHeight())
    },
    focus: function () {
      $('#' + this._uid + '-button').focus()
    }
  },
  mounted: function () {

  }
}
</script>

<style scoped>

    form.ui.custom-ui.form .field.error .FormSelect .FormSelect__control .FormSelect__button {
        border-color: #e0b4b4 !important;
    }

    form.ui.custom-ui.form .field.error .FormSelect .FormSelect__control .FormSelect__button,
    form.ui.custom-ui.form .field.error .FormSelect .FormSelect__control .FormSelect__placeholder {
        color: #9f3a38 !important;
    }

    .FormSelect__control {
        position: relative;
    }

    .FormSelect__button {
        margin: 0;
        -webkit-box-flex: 1;
        -ms-flex: 1 0 auto;
        flex: 1 0 auto;
        outline: 0;
        -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
        text-align: left;
        line-height: 20px;
        font-family: DaimlerCS-Regular, sans-serif;
        padding: 22px 12px 6px 16px;
        background: #f4f4f4;
        border: 1px solid #c7c7c7;
        color: rgba(0, 0, 0, .87);
        border-radius: 0px;
        -webkit-box-shadow: none;
        box-shadow: none;
        -webkit-appearance: none;
        width: 100%;
        display: flex;
        justify-content: space-between;
    }

    .FormSelect__button:focus {
        padding: 21px 11px 5px 15px;
        border-width: 2px;
        border-color: #52a1e9;
        border-radius: 0px;
    }

    .FormSelect__button--active {
        padding: 21px 11px 7px 15px;
        border-width: 2px;
        border-color: #52a1e9;
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-radius: 0px;
    }

    .FormSelect__placeholder {
        color: #979797;
    }

    .FormSelect__icon {
        transition: transform 0.2s;
    }

    .FormSelect__icon--rotate-180 {
        transform: rotate(180deg);
    }

    .FormSelect__options {
        overflow-x: hidden;
        overflow-y: scroll;
        height: 200px;
        cursor: auto;
        position: absolute;
        outline: 0;
        margin: 0;
        padding: 0;
        background: rgb(255, 255, 255);
        font-size: 1em;
        text-shadow: none;
        text-align: left;
        box-shadow: rgba(34, 36, 38, 0.15) 0 2px 3px 0;
        border-right-width: 2px;
        border-bottom-width: 2px;
        border-left-width: 2px;
        border-style: none solid solid;
        border-image: initial;
        border-radius: 0 0 5px 5px;
        transition: opacity 0.1s ease 0s;
        z-index: 11;
        width: 100%;
        border-top-width: initial;
        border-color: rgb(82, 161, 233) !important;
    }

    .FormSelect__option {
        height: 40px;
        line-height: 40px;
        padding-left: 16px;
        cursor: default;
        text-align: left;
    }

    .FormSelect__option:hover {
        background-color: #d6d6d6;
    }

    .FormSelect__option.has-focus {
        background-color: #80bdff;
    }

    .FormSelect__label {
        position: absolute;
        left: 17px;
        top: 5px;
        font-size: 12px;
        line-height: 12px;
        color: #676767;
        transition: color .3s;
    }

    .FormSelect__button:focus > .FormSelect__label {
        color: #52A1E9;
    }

    .FormSelect__button--active > .FormSelect__label {
        color: #52A1E9;
    }

    .u-visually-hidden {
        border: 0;
        clip: rect(0, 0, 0, 0);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        white-space: nowrap;
        width: 1px;
    }
</style>
